import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import Notice from './Notice.jsx';

/**
 * "Run now" (ENTRAID-277): starts an on-demand collection run in the
 * customer's AWS account. The server decides everything (who may run, the
 * in-progress guard, the rate limits) through GET /run-status and
 * POST /trigger-run; this component only shows that state in words, polls
 * while a run is in progress, and calls onFinished() when it reports back so
 * the page reloads its data. Every state and error is visible text, never a
 * tooltip.
 */

export const POLL_MS = 10000;
// Deployed customers only; the lifecycle notice explains every other status.
const RUNNABLE_STATUSES = new Set(['deployed', 'active']);

const clock = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const dateClock = (iso) => new Date(iso).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });

/** Why Run now is unavailable, in words, from a run-status body. */
export function blockedText(status) {
  if (!status || status.canRun || status.inProgress) return null;
  if (status.reason === 'cooldown' && status.retryAt) {
    return `Available again at ${clock(status.retryAt)}. Run now is limited to one run every ${status.limits?.cooldownMinutes ?? 10} minutes.`;
  }
  if (status.reason === 'daily-limit') {
    const limit = status.limits?.dailyLimit ?? 6;
    return `Daily limit reached: ${limit} of ${limit} runs used today.${status.retryAt ? ` Available again ${dateClock(status.retryAt)}.` : ''}`;
  }
  return status.message ?? 'Run now is not available for this customer right now.';
}

/** The message for a failed POST /trigger-run, from the API error. */
export function startErrorText(err) {
  if (err?.status === 401) return 'Your session has expired. Sign in again to run a collection.';
  if (err?.status === 403) return 'You are not allowed to start a run for this customer.';
  if (err?.status === 429 && err.body?.status) return blockedText(err.body.status);
  return err?.body?.message || err?.message || 'The run could not be started.';
}

/** True when Collect now is offered for this customer (deployed customers only). */
export const canOfferRunNow = (customer) => RUNNABLE_STATUSES.has(customer?.status);

/**
 * Props: `bare` drops the card frame so a page can embed the panel as the header of its own section (Overview's
 * "Data collection"); `heading` is the title; `summary` replaces the default sentence under the title while no run is
 * in progress. The behavior, permissions and limits are the same either way.
 */
export default function RunNow(props) {
  if (!canOfferRunNow(props.customer)) return null;
  return <RunNowPanel {...props} />;
}

/**
 * The Collect now state for one customer: the run status, polling while a run is in progress, the cooldown re-check
 * and the start action. Shared by the panel below and by a page that puts the button in its header and the status
 * under it (Data collection). `enabled` false (a customer Collect now is not offered for) makes no request.
 */
export function useRunNow(customer, onFinished, { enabled = true } = {}) {
  const customerId = customer?.customerId;
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // { tone: 'ok'|'warn'|'error', text }
  const wasRunning = useRef(false);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  function apply(next) {
    if (wasRunning.current && !next.inProgress) {
      const state = next.lastRequest?.state;
      setNotice(state === 'no-report'
        ? { tone: 'warn', text: `The run did not report back within ${next.reportWindowMinutes ?? 16} minutes. Check the collection history; if no new run appears, contact support@ipc-global.com.` }
        : { tone: 'ok', text: 'Run finished. The page now shows its results.' });
      finishedRef.current?.();
    }
    wasRunning.current = Boolean(next.inProgress);
    setStatus(next);
  }

  function load() {
    return Promise.resolve()
      .then(() => api.getRunStatus(customerId))
      .then((s) => { setLoadError(null); apply(s); })
      .catch((e) => setLoadError(e?.message || 'unknown error'));
  }

  useEffect(() => { if (enabled && customerId) load(); }, [customerId, enabled]);

  // Poll while a run is in progress.
  useEffect(() => {
    if (!status?.inProgress) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [status?.inProgress, customerId]);

  // Re-check when a 10-minute cooldown ends, so the button comes back by itself.
  useEffect(() => {
    if (status?.reason !== 'cooldown' || !status.retryAt) return undefined;
    const ms = Date.parse(status.retryAt) - Date.now() + 1000;
    if (!(ms > 0 && ms < 11 * 60000)) return undefined;
    const t = setTimeout(load, ms);
    return () => clearTimeout(t);
  }, [status?.reason, status?.retryAt, customerId]);

  async function start() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.triggerRun(customerId);
      if (res?.alreadyRunning) setNotice({ tone: 'warn', text: 'A run was already in progress, so no new run was started. Showing that one.' });
      if (res?.status) apply(res.status);
    } catch (e) {
      if (e?.body?.status) apply(e.body.status);
      // A rate limit is already explained by the blocked note the new status shows.
      if (!(e?.status === 429 && e.body?.status)) setNotice({ tone: 'error', text: startErrorText(e) });
    } finally {
      setBusy(false);
    }
  }

  const inProgress = Boolean(status?.inProgress);
  const canRun = Boolean(status?.canRun) && !busy;
  const blocked = blockedText(status);
  const last = status?.lastRequest;
  const limits = status?.limits;

  return { status, loadError, busy, notice, start, load, inProgress, canRun, blocked, last, limits };
}

function RunNowPanel({ customer, onFinished, bare = false, heading = 'Collect now', summary = null }) {
  const { status, loadError, busy, notice, start, load, inProgress, canRun, blocked, last, limits } = useRunNow(customer, onFinished);

  return (
    <div className={bare ? '' : 'card'} data-testid="run-now">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2>{heading}</h2>
          {inProgress ? (
            <p className="mt-1 text-sm text-slate-700 flex items-center gap-2" role="status">
              <svg className="w-4 h-4 animate-spin text-ipc-light flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
              <span>
                <strong>Run in progress.</strong>
                {last?.requestedAt && ` Started at ${clock(last.requestedAt)}${last.requestedBy ? ` by ${last.requestedBy}` : ''}.`}
                {' '}This page refreshes when it finishes (usually a few minutes, at most {status.reportWindowMinutes ?? 16}).
              </span>
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-500" data-testid={summary ? 'run-now-summary' : undefined}>
              {summary ?? 'Collect the latest Microsoft Entra ID data now instead of waiting for the next scheduled collection.'}
            </p>
          )}
          {limits?.appliesToCaller && !inProgress && (
            <p className="mt-1 text-xs text-slate-500" data-testid="run-now-usage">
              {limits.usedToday} of {limits.dailyLimit} runs used today (UTC), at most one every {limits.cooldownMinutes} minutes.
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={start}
          disabled={!canRun}
        >
          {busy ? 'Starting…' : inProgress ? 'Run in progress' : 'Collect now'}
        </button>
      </div>

      {!status && !loadError && <p className="mt-3 text-sm text-slate-500">Checking run status…</p>}
      {loadError && (
        <div role="alert" className="mt-3 text-sm px-3 py-2 rounded bg-red-50 text-red-700">
          Couldn't check the run status: {loadError}.{' '}
          <button type="button" className="underline" onClick={load}>Try again</button>
        </div>
      )}
      {blocked && (
        <Notice tone="warn" role="note" testId="run-now-blocked" titleAs="p" title={blocked} className="mt-3" />
      )}
      {notice && (
        <Notice
          tone={notice.tone === 'ok' ? 'good' : notice.tone === 'warn' ? 'warn' : 'bad'}
          role={notice.tone === 'error' ? 'alert' : 'status'}
          titleAs="p"
          title={notice.text}
          className="mt-3"
        />
      )}
    </div>
  );
}

/** The Collect now button alone, for a page header (`run` is a useRunNow state). */
export function RunNowButton({ run }) {
  return (
    <button type="button" className="btn-primary gap-2" onClick={run.start} disabled={!run.canRun}>
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z" /></svg>
      {run.busy ? 'Starting…' : run.inProgress ? 'Run in progress' : 'Collect now'}
    </button>
  );
}

const RULE = Object.freeze({ info: 'var(--blue)', good: 'var(--good)', warn: 'var(--warn)', bad: 'var(--bad)' });
const LINE = 'rounded-xl border border-ipc-border bg-white px-4 py-3 text-sm text-ipc-ink';
const ruled = (tone) => ({ borderLeft: `4px solid ${RULE[tone]}` });

/**
 * The Collect now state in words under a page header: the run in progress, why it is unavailable, the outcome of the
 * last start and today's usage. Each line has a left rule in the status color, never a tinted fill; the text is ink.
 */
export function RunNowStatus({ run }) {
  const { status, loadError, notice, load, inProgress, blocked, last, limits } = run;
  const usage = limits?.appliesToCaller && !inProgress;
  if (!inProgress && !loadError && !blocked && !notice && !usage) return null;
  const noticeTone = notice?.tone === 'ok' ? 'good' : notice?.tone === 'warn' ? 'warn' : 'bad';
  return (
    <div className="space-y-2" data-testid="run-now">
      {inProgress ? (
        <p className={`${LINE} flex items-center gap-2`} style={ruled('info')} role="status">
          <svg className="h-4 w-4 shrink-0 animate-spin text-ipc-light" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <span>
            <strong>Run in progress.</strong>
            {last?.requestedAt && ` Started at ${clock(last.requestedAt)}${last.requestedBy ? ` by ${last.requestedBy}` : ''}.`}
            {' '}This page refreshes when it finishes (usually a few minutes, at most {status.reportWindowMinutes ?? 16}).
          </span>
        </p>
      ) : null}
      {loadError ? (
        <div role="alert" className={LINE} style={ruled('bad')}>
          Couldn&apos;t check the run status: {loadError}.{' '}
          <button type="button" className="font-semibold text-ipc-light underline" onClick={load}>Try again</button>
        </div>
      ) : null}
      {blocked ? <div role="note" data-testid="run-now-blocked" className={LINE} style={ruled('warn')}>{blocked}</div> : null}
      {notice ? <div role={notice.tone === 'error' ? 'alert' : 'status'} className={LINE} style={ruled(noticeTone)}>{notice.text}</div> : null}
      {usage ? (
        <p className="text-[13px] text-ipc-muted" data-testid="run-now-usage">
          {limits.usedToday} of {limits.dailyLimit} runs used today (UTC), at most one every {limits.cooldownMinutes} minutes.
        </p>
      ) : null}
    </div>
  );
}
